import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Loader2, Send } from "lucide-react";
import { useSiteContent } from "@/contexts/SiteContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { content } = useSiteContent();
  const c = content.contact;
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Fill in name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        company: form.company || null,
        message: form.message,
      });
      toast.success("Sent. We'll reply within 24h.");
      setDone(true);
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="band bg-[#F2EBDC] text-[#0A0A0A] py-20 lg:py-28"
    >
      <div className="max-w-[1500px] mx-auto px-5 lg:px-8 relative">
        {/* Tape */}
        <div className="tape" style={{ top: "-10px", left: "10%", transform: "rotate(-3deg)" }} />
        <div className="tape" style={{ top: "-10px", right: "15%", transform: "rotate(4deg)" }} />

        {/* Sticker row */}
        <div className="mb-6">
          <div className="wiggle inline-block" style={{ "--r": "-4deg" }}>
            <div className="sticker bg-[#FF2D9C] text-[#FAF5E8] border-[#0A0A0A]">
              ⑤ Inbox · open
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 border-b-2 border-[#0A0A0A] pb-5 font-mono text-[10px] uppercase tracking-[0.28em]">
          <span>⑤ Contact · ≤24h reply</span>
          <span>{c.email} · Limassol, Cyprus</span>
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          data-testid="contact-title"
          className="font-display mt-10"
          style={{
            fontSize: "clamp(2.75rem, 9vw, 8rem)",
            lineHeight: 0.82,
            letterSpacing: "-0.025em",
          }}
        >
          Slide<br />
          into the <span className="font-hand text-[#FF2D9C]" style={{letterSpacing:0}}>inbox.</span>
        </motion.h2>

        <div className="grid grid-cols-12 gap-8 lg:gap-14 mt-12">
          {/* Left col */}
          <div className="col-span-12 lg:col-span-5">
            <p className="font-mono text-sm leading-relaxed max-w-md">
              {c.intro}{" "}
              <span className="font-hand text-2xl text-[#FF2D9C]">we read everything.</span>
            </p>

            <div className="mt-7 space-y-4">
              <div className="flex items-center gap-3 font-mono text-sm">
                <Mail className="w-4 h-4 text-[#FF2D9C]" />
                <a
                  href={`mailto:${c.email}`}
                  data-testid="contact-email"
                  className="bg-[#FFD500] px-1.5 hover:bg-[#FF2D9C] hover:text-[#FAF5E8] transition-colors"
                >
                  {c.email}
                </a>
              </div>
              <div data-testid="contact-address" className="font-mono text-sm leading-relaxed">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#0A0A0A]/60 mb-1">
                  Registered office
                </div>
                {c.address_line_1}<br />
                {c.address_line_2}
              </div>
            </div>

            {/* Sticker stack */}
            <div className="mt-8 flex flex-wrap gap-2.5">
              <div className="sticker bg-[#FF5B17]">★ Any platform</div>
              <div className="sticker bg-[#00E5A0]">★ Any timezone</div>
              <div className="sticker bg-[#FFD500]">★ Worldwide</div>
            </div>
          </div>

          {/* Right col – form */}
          <div className="col-span-12 lg:col-span-7">
            {done ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                data-testid="contact-form-success"
                className="bg-[#0A0A0A] text-[#FAF5E8] border-2 border-[#0A0A0A] rounded-2xl p-10 lg:p-14"
                style={{ boxShadow: "8px 8px 0 #FF2D9C" }}
              >
                <div className="font-display text-6xl text-[#FFD500] leading-none mb-3">
                  Sent ✓
                </div>
                <p className="font-hand text-3xl text-[#FF2D9C]">
                  see you in the inbox
                </p>
                <p className="font-mono text-sm mt-6 text-[#FAF5E8]/80 max-w-md">
                  Your message landed on our desk. We&apos;ll be back within
                  24h, no matter the timezone.
                </p>
                <button
                  type="button"
                  data-testid="contact-form-reset"
                  onClick={() => setDone(false)}
                  className="mt-8 inline-flex items-center gap-2 bg-[#FAF5E8] text-[#0A0A0A] px-5 py-2.5 rounded-full font-mono text-xs uppercase tracking-[0.2em] border-2 border-[#FAF5E8] hover:bg-transparent hover:text-[#FAF5E8] transition-colors"
                >
                  Send another →
                </button>
              </motion.div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="bg-[#FAF5E8] border-2 border-[#0A0A0A] rounded-2xl p-8 lg:p-10 space-y-6"
                style={{ boxShadow: "8px 8px 0 #0A0A0A" }}
              >
                <div>
                  <label
                    htmlFor="contact-name"
                    className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#0A0A0A]/70"
                  >
                    ① Name *
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={handleChange}
                    placeholder="your name here"
                    className="input-zine"
                    data-testid="contact-input-name"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="contact-email"
                      className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#0A0A0A]/70"
                    >
                      ② Email *
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@brand.com"
                      className="input-zine"
                      data-testid="contact-input-email"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contact-company"
                      className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#0A0A0A]/70"
                    >
                      ③ Company / Handle
                    </label>
                    <input
                      id="contact-company"
                      name="company"
                      type="text"
                      value={form.company}
                      onChange={handleChange}
                      placeholder="@yourhandle"
                      className="input-zine"
                      data-testid="contact-input-company"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="contact-message"
                    className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#0A0A0A]/70"
                  >
                    ④ Tell us everything *
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={4}
                    required
                    value={form.message}
                    onChange={handleChange}
                    placeholder="the project, the platform, the dream..."
                    className="input-zine resize-none"
                    data-testid="contact-input-message"
                  />
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-form-submit"
                    className="btn-chunky disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending
                      </>
                    ) : (
                      <>
                        Send it
                        <Send className="w-4 h-4" />
                      </>
                    )}
                  </button>
                  <span className="font-hand text-xl text-[#FF2D9C]">
                    we don&apos;t bite ↗
                  </span>
                </div>
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#0A0A0A]/50">
                  By submitting you agree to be contacted by RTAMA LTD.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
